/**
 * Builder for the Captello capture webview embed URL.
 *
 * The webview reads its configuration from query-string params. The short keys
 * below are the contract the webview expects (see the webview's `PARAMS` enum);
 * this builder maps friendly option names onto those keys so hosts never have to
 * hard-code `"f"`, `"m"`, etc.
 */

/** Query-param keys understood by the webview. */
export enum EmbedParam {
    FormId = "f",
    SubmissionToken = "s",
    StationId = "st",
    Mode = "m",
    EventWebAccessToken = "e",
    ActivationId = "a",
    Language = "l",
    ActionButtonPosition = "b",
    FormType = "form_type",
    Launcher = "launcher",
    SubmissionType = "t",
    SubmitButtonBottomPadding = "sbbp",
    Platform = "platform",
    HideEmail = "he",
    ConnexionsEmbedMode = "cem",
    /** Edit mode read-only: locks email-mapped and invitation-code elements. */
    Emro = "emro",
    /** Context for filtering form-fill actions (e.g. MMP outbound/inbound/notes). */
    UseIn = "useIn",
    /**
     * Show the transcribe button: the form renders a button that emits a
     * `host_processing_request` with `processing_type: "transcribe_scanner_request"`.
     * Only enable it when the host answers those requests (e.g. via
     * `CaptelloWebview.onProcessingRequest`).
     */
    ShowTranscribeButton = "show_transcribe_button",
}

/** Form render mode (the webview's `FormMode`). */
export enum FormMode {
    Preview = "preview",
    Submit = "submit",
    Edit = "edit",
    View = "view",
}

/** Identifies the host embedding the webview (the webview's `LAUNCHER_TYPES`). */
export enum LauncherType {
    EventGenMobile = "event_gen_mobile",
    EventGenWeb = "event_gen_web",
    WebApp = "webapp",
    Mmp = "MMP",
}

/** `form_type` discriminator. */
export type FormType = "template" | "device";

/** `t` (submission type) discriminator. */
export type SubmissionType = "normal" | "drafted";

/**
 * Action-button position param (`b`), mirroring the webview's `CTABtnPosition`.
 * The wire values are numeric strings; use {@link ActionButtonPosition} for the
 * readable names.
 */
export enum ActionButtonPosition {
    Fixed = "0",
    Bottom = "1",
    Hidden = "2",
}

/** Supported webview languages. The wire value is the two-letter code. */
export enum Language {
    Arabic = "ar",
    German = "de",
    English = "en",
    Spanish = "es",
    French = "fr",
    Italian = "it",
    Japanese = "ja",
    Korean = "ko",
    Dutch = "nl",
    Portuguese = "pt",
    Chinese = "zh",
}

/** Context for filtering form-fill actions, sent as the `useIn` param. */
export type UseInContext = "outbound" | "inbound" | "notes";

/**
 * Options for {@link buildEmbedUrl}. Every field is optional; only the ones you set
 * are written to the URL. `formId` is effectively required for a real embed but is
 * left optional so callers can build preview/partial URLs.
 */
export interface EmbedUrlOptions {
    formId?: string | number;
    submissionToken?: string;
    stationId?: string | number;
    mode?: FormMode;
    eventWebAccessToken?: string;
    activationId?: string | number;
    language?: Language;
    actionButtonPosition?: ActionButtonPosition;
    formType?: FormType;
    launcher?: LauncherType;
    submissionType?: SubmissionType;
    submitButtonBottomPadding?: string | number;
    /** Context for filtering form-fill actions (the `useIn` param). */
    useIn?: UseInContext;
    platform?: "web" | "mobile";
    hideEmail?: boolean;
    /** Connexions embed mode: suppress in-webview redirect/vCard download. */
    connexionsEmbedMode?: boolean;
    /** Edit mode read-only. */
    emro?: boolean;
    /**
     * Show the transcribe button in the form (emits `host_processing_request` with
     * `processing_type: "transcribe_scanner_request"` when pressed). Enable only when
     * the host answers those requests (e.g. via `CaptelloWebview.onProcessingRequest`) —
     * otherwise the button times out with an error for the user.
     */
    showTranscribeButton?: boolean;
    /**
     * Extra query params to append verbatim (e.g. prospect tracking params the
     * webview forwards on submit). Values are stringified; `undefined`/`null` skipped.
     */
    extraParams?: Record<string, string | number | boolean | undefined | null>;
}

// Maps each option onto its query key. Order here defines the order params are
// written, which keeps generated URLs stable and diffable.
const OPTION_TO_PARAM: ReadonlyArray<[keyof EmbedUrlOptions, EmbedParam]> = [
    ["formId", EmbedParam.FormId],
    ["submissionToken", EmbedParam.SubmissionToken],
    ["stationId", EmbedParam.StationId],
    ["mode", EmbedParam.Mode],
    ["eventWebAccessToken", EmbedParam.EventWebAccessToken],
    ["activationId", EmbedParam.ActivationId],
    ["language", EmbedParam.Language],
    ["actionButtonPosition", EmbedParam.ActionButtonPosition],
    ["formType", EmbedParam.FormType],
    ["launcher", EmbedParam.Launcher],
    ["submissionType", EmbedParam.SubmissionType],
    ["submitButtonBottomPadding", EmbedParam.SubmitButtonBottomPadding],
    ["useIn", EmbedParam.UseIn],
    ["platform", EmbedParam.Platform],
];

// Boolean flags are encoded as "1" when true and omitted when false/unset, matching
// how the webview reads them (`Boolean(queryParams[key])` / presence checks).
const BOOLEAN_OPTION_TO_PARAM: ReadonlyArray<[keyof EmbedUrlOptions, EmbedParam]> = [
    ["hideEmail", EmbedParam.HideEmail],
    ["connexionsEmbedMode", EmbedParam.ConnexionsEmbedMode],
    ["emro", EmbedParam.Emro],
    ["showTranscribeButton", EmbedParam.ShowTranscribeButton],
];

/** The path the capture webview is served at. The SDK owns this so callers don't. */
export const CAPTURE_SUBMISSION_PATH = "/capture/submission";

// Recognized capture routes — if the base URL already targets one of these, it is kept
// as-is; otherwise the canonical submission path is appended. `capture/activation` is
// preserved so activation embeds aren't rewritten to a submission URL.
const CAPTURE_PATH_PATTERN = /\/capture\/(submission|activation)\/?$/;

/**
 * Normalizes a base capture URL so the path is always a valid capture route, no matter
 * what the caller passed. This is the fix for the "do I include `/capture/submission`?"
 * footgun: the origin, the origin with a trailing slash, and the full path all converge
 * to the same correct URL.
 *
 * - Origin only (`https://capture.captello.com`) → path set to `/capture/submission`.
 * - Already a capture route (`…/capture/submission`, `…/capture/activation`, with or
 *   without a trailing slash) → kept (trailing slash trimmed).
 * - A base path (`https://host/webview`) → `/capture/submission` appended to it, so
 *   sub-path deployments still work.
 *
 * Existing query params and the origin are always preserved.
 */
function normalizeCaptureUrl(baseUrl: string): URL {
    const url = new URL(baseUrl);

    if (CAPTURE_PATH_PATTERN.test(url.pathname)) {
        url.pathname = url.pathname.replace(/\/+$/, ""); // drop any trailing slash
        return url;
    }

    const base = url.pathname.replace(/\/+$/, ""); // "" for origin/"/", "/webview" for a sub-path
    url.pathname = `${base}${CAPTURE_SUBMISSION_PATH}`;
    return url;
}

/**
 * Builds an absolute embed URL from a capture base URL and typed options.
 *
 * You only need to pass the **capture origin** — the SDK appends the capture path for
 * you. Passing the origin, the origin with a trailing slash, or the full
 * `…/capture/submission` URL all produce the same correct result, so there's nothing to
 * get wrong. Existing query params on `baseUrl` are preserved; options override params
 * with the same key.
 *
 * @example
 * // All three are equivalent:
 * buildEmbedUrl("https://capture.captello.com", { formId: 1234, mode: FormMode.Submit });
 * buildEmbedUrl("https://capture.captello.com/", { formId: 1234, mode: FormMode.Submit });
 * buildEmbedUrl("https://capture.captello.com/capture/submission", { formId: 1234, mode: FormMode.Submit });
 * // → "https://capture.captello.com/capture/submission?f=1234&m=submit"
 */
export function buildEmbedUrl(baseUrl: string, options: EmbedUrlOptions = {}): string {
    const url = normalizeCaptureUrl(baseUrl);

    for (const [optionKey, paramKey] of OPTION_TO_PARAM) {
        const value = options[optionKey];
        if (value !== undefined && value !== null && value !== "") {
            url.searchParams.set(paramKey, String(value));
        }
    }

    for (const [optionKey, paramKey] of BOOLEAN_OPTION_TO_PARAM) {
        if (options[optionKey]) {
            url.searchParams.set(paramKey, "1");
        }
    }

    if (options.extraParams) {
        for (const [key, value] of Object.entries(options.extraParams)) {
            if (value !== undefined && value !== null) {
                url.searchParams.set(key, String(value));
            }
        }
    }

    return url.toString();
}
